import { useResetOnClose } from "@/lib/useResetOnClose";
import { canLogDelays } from "@/lib/roles";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, CalendarClock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const DELAY_CATEGORIES = ["WEATHER", "MATERIAL", "LABOUR", "CLIENT", "DESIGN", "STATUTORY", "OTHER"];

export default function Delays() {
  const { projectId, isProjectManager, projectRole } = useProjectCtx();
  const canLog = canLogDelays(projectRole);          // the site team logs and resolves delays; applying one to the schedule stays with the project manager
  const [delays, setDelays] = useState([]);
  const [wbsItems, setWbsItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyTarget, setApplyTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: rows }, { data: wbs }, { data: sum }] = await Promise.all([
        api.get(`/projects/${projectId}/delays`),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }),
        api.get(`/projects/${projectId}/delays/summary`),
      ]);
      setDelays(rows);
      setWbsItems(wbs);
      setSummary(sum);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const itemLabel = (id) => {
    const item = wbsItems.find((w) => w.id === id);
    return item ? `${item.code} — ${item.name}` : "—";
  };

  const resolve = async (delay) => {
    setBusyId(delay.id);
    try {
      await api.patch(`/projects/${projectId}/delays/${delay.id}`, { status: "RESOLVED", resolved_date: todayISO() });
      toast.success("Delay marked resolved");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const confirmApply = async () => {
    if (!applyTarget) return;
    setBusyId(applyTarget.id);
    try {
      await api.post(`/projects/${projectId}/delays/${applyTarget.id}/apply-to-schedule`, { confirm: true });
      toast.success("Schedule shifted by the delay's impact days");
      setApplyTarget(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="delays-page">
      <PageHeader
        title="Delays"
        description="Extension-of-time register. Applying a delay shifts the linked activity's forecast end date."
        action={canLog && <CreateDelayDialog projectId={projectId} wbsItems={wbsItems} onCreated={load} />}
      />

      {summary.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="delay-summary-cards">
          {summary.map((s) => (
            <div key={s.category} className="rounded-xl border border-border bg-card p-4" data-testid={`delay-summary-card-${s.category}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.category}</p>
              <p className="mt-2 font-mono text-2xl font-bold text-foreground">{s.total_impact_days}d</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{s.delay_count} delay{s.delay_count === 1 ? "" : "s"}</p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card">
        {!loading && delays.length === 0 ? (
          <EmptyState title="No delays logged" description="Log a delay to track its schedule impact." testId="delays-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Raised</TableHead><TableHead>Activity</TableHead><TableHead>Category</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Impact (days)</TableHead><TableHead>Status</TableHead><TableHead>Applied</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {delays.map((d) => (
                <TableRow key={d.id} data-testid={`delay-row-${d.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(d.raised_date)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{d.wbs_item_id ? itemLabel(d.wbs_item_id) : "—"}</TableCell>
                  <TableCell className="text-xs text-foreground">{d.category}</TableCell>
                  <TableCell className="max-w-[220px] text-xs text-muted-foreground">{d.reason}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-rose-400">{d.impact_days}</TableCell>
                  <TableCell><StatusPill value={d.status} /></TableCell>
                  <TableCell>{d.applied_to_schedule ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {canLog && d.status === "OPEN" && (
                        <Button size="sm" variant="ghost" disabled={busyId === d.id} onClick={() => resolve(d)} data-testid={`resolve-delay-button-${d.id}`}>Resolve</Button>
                      )}
                      {isProjectManager && !d.applied_to_schedule && d.wbs_item_id && (
                        <Button size="sm" variant="outline" disabled={busyId === d.id} onClick={() => setApplyTarget(d)} data-testid={`apply-delay-button-${d.id}`}>
                          <CalendarClock className="h-3.5 w-3.5" /> Apply
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!applyTarget} onOpenChange={(o) => !o && setApplyTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="apply-delay-confirm-dialog">
          <DialogHeader><DialogTitle>Apply delay to schedule?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This shifts the activity's forecast end date forward by <span className="font-semibold text-foreground">{applyTarget?.impact_days} day(s)</span>. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyTarget(null)} data-testid="apply-delay-cancel-button">Cancel</Button>
            <Button disabled={busyId === applyTarget?.id} onClick={confirmApply} data-testid="apply-delay-confirm-button">
              {busyId === applyTarget?.id && <Loader2 className="h-4 w-4 animate-spin" />} Yes, shift schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateDelayDialog({ projectId, wbsItems, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ wbs_item_id: "", raised_date: todayISO(), category: "OTHER", reason: "", impact_days: "" });
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm({ wbs_item_id: "", raised_date: todayISO(), category: "OTHER", reason: "", impact_days: "" }); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/delays`, { ...form, wbs_item_id: form.wbs_item_id || null, impact_days: Number(form.impact_days) });
      toast.success("Delay logged");
      setForm({ wbs_item_id: "", raised_date: todayISO(), category: "OTHER", reason: "", impact_days: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-delay-button"><Plus className="h-4 w-4" /> Log Delay</Button></DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Log a Delay</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-delay-form">
          <div>
            <Label>Activity (optional — required to apply to schedule)</Label>
            <Select value={form.wbs_item_id || "__none__"} onValueChange={(v) => setForm({ ...form, wbs_item_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="delay-activity-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not linked</SelectItem>
                {wbsItems.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Raised date</Label><Input required type="date" value={form.raised_date} onChange={set("raised_date")} className="mt-1.5" data-testid="delay-raised-date-input" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="delay-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DELAY_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Impact (days)</Label><Input required type="number" min="1" value={form.impact_days} onChange={set("impact_days")} className="mt-1.5" data-testid="delay-impact-days-input" /></div>
          </div>
          <div><Label>Reason</Label><Textarea required value={form.reason} onChange={set("reason")} className="mt-1.5" data-testid="delay-reason-input" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-delay-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
